import { StatusBadge, type StatusTone } from '@skylabs-digital/backoffice-kit';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { ERROR_CATEGORY_INFO } from '../../constants/error-categories';
import type { DeliveryAttempt } from '../../schemas/notification.schema';
import { statusTone } from '../../utils/status-tone';
import { JsonViewer } from '../shared/JsonViewer';

interface DeliveryAttemptTimelineProps {
  attempts: DeliveryAttempt[];
}

const TONE_DOT: Record<StatusTone, string> = {
  neutral: 'bg-muted-foreground',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-destructive',
  accent: 'bg-accent',
};

/** Provider message ID, monospaced and copiable — operators paste it into Resend's dashboard. */
function CopyableProviderMsgId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — nothing to recover from.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy provider message ID"
      className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
    >
      {value}
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

export function DeliveryAttemptTimeline({ attempts }: DeliveryAttemptTimelineProps) {
  if (attempts.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">No delivery attempts yet.</p>
    );
  }

  const sorted = [...attempts].sort((a, b) => b.attempt - a.attempt);

  return (
    <ol className="relative space-y-6 border-l border-border pl-6">
      {sorted.map((attempt) => {
        const tone = statusTone(attempt.status);
        const categoryInfo = attempt.errorCategory
          ? ERROR_CATEGORY_INFO[attempt.errorCategory]
          : null;

        return (
          <li key={attempt.id} className="relative">
            <span
              className={`absolute -left-[31px] top-1 inline-block h-3.5 w-3.5 rounded-full ring-4 ring-background ${TONE_DOT[tone]}`}
              aria-hidden="true"
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                Attempt #{attempt.attempt}
              </span>
              <span className="text-xs text-muted-foreground">via {attempt.provider}</span>
              <StatusBadge tone={tone}>{attempt.status}</StatusBadge>
              {categoryInfo ? (
                <StatusBadge tone={categoryInfo.tone} title={categoryInfo.description}>
                  {categoryInfo.label}
                </StatusBadge>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {attempt.sentAt
                ? new Date(attempt.sentAt).toLocaleString()
                : new Date(attempt.createdAt).toLocaleString()}
            </p>
            {attempt.errorMessage ? (
              <p className="mt-1 text-sm text-destructive">{attempt.errorMessage}</p>
            ) : null}
            {attempt.providerErrorCode ? (
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                Provider error: {attempt.providerErrorCode}
              </p>
            ) : null}
            {attempt.providerMsgId ? (
              <p className="mt-1">
                <CopyableProviderMsgId value={attempt.providerMsgId} />
              </p>
            ) : null}
            {attempt.nextRetryAt ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Next retry at {new Date(attempt.nextRetryAt).toLocaleString()}
              </p>
            ) : null}
            {attempt.retryReason ? (
              <p className="mt-1 text-xs text-muted-foreground">Retry: {attempt.retryReason}</p>
            ) : null}
            {attempt.providerResponse && Object.keys(attempt.providerResponse).length > 0 ? (
              <div className="mt-2">
                <JsonViewer data={attempt.providerResponse} label="Provider response" />
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
